import { Control, useController } from 'react-hook-form'
import { useSnackbar } from 'notistack'

import Skeleton from '@mui/material/Skeleton'
import Box from '@mui/material/Box'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'

// ค่า config สำหรับการ upload avatar
const AVATAR_CONFIG = {
	maxFileSize: 1 * 1024 * 1024, // 1MB
	maxFileSizeLabel: '1MB',
	allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
	allowedTypesLabel: 'JPG, PNG, WebP, GIF',
	maxDimension: 500, // resize to max 500x500
	quality: 0.85, // compression quality
}

export interface IUserAvatarProps {
	id: string
	control: Control<any, any>
	name: string
	loading?: boolean
}

// Helper function สำหรับ resize และ compress รูปภาพ
const resizeAndCompressImage = (file: File, maxDimension: number, quality: number): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = (e) => {
			const img = new Image()
			img.onload = () => {
				const canvas = document.createElement('canvas')
				let { width, height } = img

				// Calculate new dimensions while maintaining aspect ratio
				if (width > height) {
					if (width > maxDimension) {
						height = Math.round((height * maxDimension) / width)
						width = maxDimension
					}
				} else {
					if (height > maxDimension) {
						width = Math.round((width * maxDimension) / height)
						height = maxDimension
					}
				}

				canvas.width = width
				canvas.height = height

				const ctx = canvas.getContext('2d')
				if (!ctx) {
					reject(new Error('Failed to get canvas context'))
					return
				}

				ctx.drawImage(img, 0, 0, width, height)

				// Convert to base64 with compression
				const dataUrl = canvas.toDataURL('image/jpeg', quality)
				resolve(dataUrl)
			}
			img.onerror = () => reject(new Error('Failed to load image'))
			img.src = e.target?.result as string
		}
		reader.onerror = () => reject(new Error('Failed to read file'))
		reader.readAsDataURL(file)
	})
}

export default function UserAvatar({
	id,
	control,
	name,
	loading,
	...other
}: IUserAvatarProps) {
	const { field } = useController({ control, name })
	const { enqueueSnackbar } = useSnackbar()

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		// ตรวจสอบประเภทไฟล์
		if (!AVATAR_CONFIG.allowedTypes.includes(file.type)) {
			enqueueSnackbar(`รองรับเฉพาะไฟล์ ${AVATAR_CONFIG.allowedTypesLabel} เท่านั้น`, { variant: 'warning' })
			e.target.value = ''
			return
		}

		// ตรวจสอบขนาดไฟล์
		if (file.size > AVATAR_CONFIG.maxFileSize) {
			enqueueSnackbar(`ขนาดไฟล์ต้องไม่เกิน ${AVATAR_CONFIG.maxFileSizeLabel}`, { variant: 'warning' })
			e.target.value = ''
			return
		}

		try {
			// Resize และ compress รูปภาพ
			const compressedImage = await resizeAndCompressImage(
				file,
				AVATAR_CONFIG.maxDimension,
				AVATAR_CONFIG.quality
			)
			field.onChange(compressedImage)
			enqueueSnackbar('อัพโหลดรูปโปรไฟล์สำเร็จ', { variant: 'success' })
		} catch (error) {
			console.error('Error processing image:', error)
			enqueueSnackbar('เกิดข้อผิดพลาดในการประมวลผลรูปภาพ', { variant: 'error' })
		}

		e.target.value = ''
	}

	return (
		<Box
			height={{ xs: 100, md: 150 }}
			width={{ xs: 100, md: 150 }}
			bgcolor={'grey.300'}
			borderRadius={'1000px'}
			overflow={'visible'}
			position={'relative'}
			border={'1px solid'}
			borderColor={'divider'}
		>
			{loading && (
				<Skeleton variant='rectangular' width={'100%'} height={'100%'} />
			)}
			{field.value && (
				<Box
					component='img'
					height={{ xs: 100, md: 150 }}
					width={{ xs: 100, md: 150 }}
					borderRadius={'1000px'}
					position={'absolute'}
					// width={'100%'}
					// height={'100%'}
					top={0}
					left={0}
					src={field.value}
					alt='avatar'
					sx={{ objectFit: 'cover' }}
				/>
			)}
			<Box
				component='input'
				type='file'
				accept={AVATAR_CONFIG.allowedTypes.join(',')}
				position={'absolute'}
				width={'100%'}
				height={'100%'}
				onChange={handleChange}
				zIndex={2}
				sx={{ opacity: 0, cursor: 'pointer' }}
			/>
			<Box position={'absolute'} bottom={0} right={0} p={1} zIndex={1}>
				<AddAPhotoIcon sx={{ color: 'primary.dark' }} />
			</Box>
		</Box>
	)
}
